import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { setSection } from "../../../assignmentSlice";

import { useTTS } from "../../../../../hooks/useTTS";

import CorrectOverlay from "../../widgets/CorrectOverlay";
import VocaMatchingCard from "./VocaMatchingCard";

const VocaMatchingPage = ({
  currentPage,
  section,
  pageList,
  setPageList,
  currentIndex,
  setCurrentIndex,
  type,
  isReview = false,
}) => {
  const dispatch = useDispatch();
  const { speak, waitForCurrentSpeech } = useTTS();

  const [engList, setEngList] = useState([]);
  const [korList, setKorList] = useState([]);
  const [selectedKorIndex, setSelectedKorIndex] = useState(-1);
  const [selectedEngIndex, setSelectedEngIndex] = useState(-1);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  useEffect(() => {
    if (currentPage.data) {
      setEngList(
        currentPage.data
          .map((word, index) => {
            return { index: index, text: word.eng, isMatched: word.isMatched };
          })
          .sort(() => Math.random() - 0.5)
      );
      setKorList(
        currentPage.data
          .map((word, index) => {
            return { index: index, text: word.kor, isMatched: word.isMatched };
          })
          .sort(() => Math.random() - 0.5)
      );
    }
  }, [currentIndex]);

  useEffect(() => {
    if (currentPage.data && engList.length > 0) {
      // 앞 페이지의 카드가 아직 남아 있을 수 있다. 화살표로 넘기면 pageList 와 currentIndex 가
      // 한 번에 바뀌어, 5장짜리 앞 페이지 카드를 1장짜리 다음 페이지 데이터로 읽다가
      // undefined 에서 터져 화면이 하얗게 됐다(2026-09-28 더슈프림 53802). 부모가 페이지마다
      // key 로 새로 그리지만, 한 번 더 막아 둔다.
      const matchedOf = (word) => currentPage.data[word.index]?.isMatched ?? word.isMatched;
      const newEngList = engList.map((word) => ({ ...word, isMatched: matchedOf(word) }));
      const newKorList = korList.map((word) => ({ ...word, isMatched: matchedOf(word) }));
      setEngList(newEngList);
      setKorList(newKorList);

      if (currentPage.isSubmitted && currentIndex < pageList.length - 1) {
        const nextIndex = currentIndex + 1;
        (async () => {
          await waitForCurrentSpeech();
          setCurrentIndex(nextIndex);
        })();
      }
    }
  }, [pageList]);

  const selectKor = (index) => {
    if (selectedKorIndex === index) {
      setSelectedKorIndex(-1);
    } else {
      setSelectedKorIndex(index);
      if (selectedEngIndex !== -1) {
        if (selectedEngIndex === index) {
          const newPageList = pageList.map((page, pageIndex) => {
            if (pageIndex === currentIndex) {
              return {
                ...page,
                data: page.data.map((word, wordIndex) => {
                  if (wordIndex === index) {
                    return { ...word, isMatched: true };
                  }
                  return word;
                }),
                isSubmitted: page.data
                  .filter((word, wordIndex) => wordIndex !== index)
                  .every((word) => word.isMatched),
              };
            }
            return page;
          });
          setIsCorrect(true);
          setPageList(newPageList);
          dispatch(setSection({ ...section, answer: newPageList }));
        } else {
          setIsCorrect(false);
        }
        setOverlayVisible(true);
        setTimeout(() => {
          setOverlayVisible(false);
        }, 600);
        setSelectedKorIndex(-1);
        setSelectedEngIndex(-1);
      }
    }
  };

  const selectEng = (index) => {
    if (selectedEngIndex === index) {
      setSelectedEngIndex(-1);
    } else {
      setSelectedEngIndex(index);
      if (selectedKorIndex !== -1) {
        if (selectedKorIndex === index) {
          setIsCorrect(true);
          const newPageList = pageList.map((page, pageIndex) => {
            if (pageIndex === currentIndex) {
              return {
                ...page,
                data: page.data.map((word, wordIndex) => {
                  if (wordIndex === index) {
                    return { ...word, isMatched: true };
                  }
                  return word;
                }),
                isSubmitted: page.data
                  .filter((word, wordIndex) => wordIndex !== index)
                  .every((word) => word.isMatched),
              };
            }
            return page;
          });
          setPageList(newPageList);
          dispatch(setSection({ ...section, answer: newPageList }));
        } else {
          setIsCorrect(false);
        }
        setOverlayVisible(true);
        setTimeout(() => {
          setOverlayVisible(false);
        }, 600);
        setSelectedKorIndex(-1);
        setSelectedEngIndex(-1);
      }
    }
    speak({ text: currentPage.data[index].eng });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "20px",
        // 화살표 사이에 낀 본문이라 폭을 스스로 가져야 한다. 없으면 flex 줄에서
        // 내용물 최소폭(break-word 라 글자 1개)까지 쪼그라들어 카드가 세로
        // 슬리버로 부서졌다 (2026-08-27, 빌드마다 CSS 순서 따라 됐다 안 됐다 함).
        flex: 1,
        minWidth: 0,
      }}
    >
      <div style={{ fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "14px" : "20px", fontWeight: 700, textAlign: "center" }}>올바른 단어와 뜻 짝을 찾아 선택하세요.</div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: Math.min(window.innerWidth, window.screen.width) <= 768 ? "6px" : "10px",
          width: "100%",
        }}
      >
        {engList.map((eng) => (
          <VocaMatchingCard
            word={eng}
            selectedIndex={selectedEngIndex}
            handleClick={selectEng}
            type={type}
          />
        ))}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: Math.min(window.innerWidth, window.screen.width) <= 768 ? "6px" : "10px",
          width: "100%",
        }}
      >
        {korList.map((kor) => (
          <VocaMatchingCard
            word={kor}
            selectedIndex={selectedKorIndex}
            handleClick={selectKor}
            type={type}
          />
        ))}
      </div>
      <div
        style={{
          opacity: overlayVisible ? 1 : 0, // Fade-in/out
          visibility: overlayVisible ? "visible" : "hidden", // DOM에서 숨김
          transition: "opacity 200ms ease-out, visibility 200ms",
        }}
      >
        <CorrectOverlay bool={isCorrect} />
      </div>
    </div>
  );
};

export default VocaMatchingPage;
